import { createFileRoute } from "@tanstack/react-router";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { SubpageHero } from "@/components/subpages/SubpageHero";
import { KpiGrid, BarChart, ReportSection } from "@/components/subpages/ReportPreview";
import { ComparisonTable } from "@/components/subpages/ComparisonTable";
import { FaqList } from "@/components/subpages/FaqList";
import { SubpageCta } from "@/components/subpages/SubpageCta";

export const Route = createFileRoute("/ergebnis")({
  head: () => ({
    meta: [
      { title: "Ihr Ergebnis – Management-Dashboard & Analysebericht" },
      {
        name: "description",
        content:
          "So sieht Ihr Ergebnis aus: Analysebericht, Förder-, Finanzierungs- und Steuerübersicht, Wirtschaftlichkeit über 10/20/30 Jahre und klare Handlungsempfehlung.",
      },
      { property: "og:title", content: "Was erhalten Sie als Ergebnis?" },
      { property: "og:description", content: "Vollständige Analyse, individuelle Handlungs­empfehlung, 10/20/30-Jahres-Prognose." },
      { property: "og:url", content: "https://www.energie-prime.de/ergebnis" },
    ],
    links: [{ rel: "canonical", href: "https://www.energie-prime.de/ergebnis" }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <main>
        <SubpageHero
          eyebrow="Kachel 4 · Ergebnis"
          title="Ihr Ergebnis."
          highlight="Das Management-Dashboard."
          subtitle="Vollständige Analyse, klare Handlungs­empfehlung, dokumentierte Entscheidungsgrundlage – alles in einem Bericht."
        />

        <section className="border-b border-[var(--rule)] surface-cloud">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-14 md:py-20 space-y-8">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="eyebrow text-[var(--leaf)] mb-2">Beispielbericht</div>
                <h2 className="font-display text-2xl md:text-4xl text-[var(--ink)]">EFH Musterstraße 12 · 8,2 kWp + 8 kWh + Luft-WP</h2>
              </div>
              <div className="text-sm text-[var(--ink)]/60">Variante C empfohlen · Energy Prime Score 87</div>
            </div>

            <KpiGrid
              kpis={[
                { label: "Investition", value: "42.400 €", hint: "brutto inkl. Montage" },
                { label: "Förderung gesamt", value: "11.800 €", hint: "BEG + KfW + Land" },
                { label: "Pay-Back", value: "11,4 Jahre" },
                { label: "Saldo nach 30 J.", value: "+38.500 €" },
              ]}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ReportSection eyebrow="Projektanalyse" title="Energiebilanz heute vs. nach Umsetzung">
                <BarChart
                  bars={[
                    { label: "Strombezug heute", value: 4800, color: "var(--ember)" },
                    { label: "Strombezug neu", value: 1400, color: "var(--leaf)" },
                    { label: "Eigenverbrauch", value: 3600, color: "var(--leaf-deep)" },
                    { label: "Einspeisung", value: 4000, color: "var(--gold)" },
                  ]}
                />
              </ReportSection>

              <ReportSection eyebrow="Fördermittel" title="Zuschüsse, Tilgungs­nachlässe">
                <BarChart
                  bars={[
                    { label: "BEG EM", value: 6500 },
                    { label: "KfW 270 Tilgung", value: 1800 },
                    { label: "Landeszuschuss", value: 1500 },
                    { label: "Steuer §35c", value: 2000 },
                  ]}
                />
              </ReportSection>

              <ReportSection eyebrow="Angebotsvergleich" title="Drei Angebote, ein Sieger">
                <ComparisonTable
                  columns={["A", "B", "C"]}
                  rows={[
                    { criterion: "Endpreis", cells: ["24.800 €", "19.900 €", "27.300 €"] },
                    { criterion: "€/kWp", cells: ["1.490", "1.580", "1.610"] },
                    { criterion: "Garantie", cells: ["25 J", "12 J", "20 J"] },
                    { criterion: "Score", cells: ["87", "62", "78"] },
                  ]}
                />
              </ReportSection>

              <ReportSection eyebrow="Wirtschaftlichkeit" title="TCO 10 / 20 / 30 Jahre">
                <BarChart
                  bars={[
                    { label: "10 Jahre", value: 4200 },
                    { label: "20 Jahre", value: 19800 },
                    { label: "30 Jahre", value: 38500 },
                  ]}
                />
              </ReportSection>

              <ReportSection eyebrow="Steueranalyse" title="Optimaler Hebel">
                <p className="text-sm text-[var(--ink)]/80 leading-relaxed">
                  §35c EStG: 20 % über 3 Jahre, max. 40.000 € – passend für selbstgenutztes EFH. Alternativ
                  bei Vermietung: lineare AfA + IAB.
                </p>
              </ReportSection>

              <ReportSection eyebrow="Chancen & Risiken" title="Sensitivität">
                <BarChart
                  bars={[
                    { label: "Strompreis +3 %/a", value: 6200, color: "var(--leaf)" },
                    { label: "Basisszenario", value: 0, color: "var(--ink-soft)" },
                    { label: "Strompreis −1 %/a", value: 3100, color: "var(--ember)" },
                  ]}
                  max={6200}
                />
              </ReportSection>
            </div>

            <ReportSection eyebrow="Handlungs­empfehlung" title="Entscheidungs­vorlage">
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[var(--ink)]/85">
                <li>Variante C umsetzen (PV 8,2 kWp + 8 kWh Speicher + Luft-WP).</li>
                <li>Förderantrag BEG EM stellen, KfW 270 parallel.</li>
                <li>Steuerlich §35c geltend machen.</li>
                <li>Re-Check nach 12 Monaten gegen Realdaten.</li>
              </ol>
            </ReportSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ReportSection title="Prognose 10 Jahre"><div className="font-display text-2xl text-[var(--leaf)]">+4.200 €</div></ReportSection>
              <ReportSection title="Prognose 20 Jahre"><div className="font-display text-2xl text-[var(--leaf)]">+19.800 €</div></ReportSection>
              <ReportSection title="Prognose 30 Jahre"><div className="font-display text-2xl text-[var(--leaf)]">+38.500 €</div></ReportSection>
            </div>
          </div>
        </section>

        <FaqList
          items={[
            { q: "Wie verbindlich sind die Zahlen?", a: "Sie basieren auf aktuellen Marktdaten, geprüften Annahmen und Ihrer Projektaufnahme. Konkrete Angebote ersetzen sie nicht – wir vergleichen sie aber dagegen." },
            { q: "In welchem Format erhalte ich den Bericht?", a: "Online im Dashboard und als PDF zum Download." },
            { q: "Kann ich Varianten ändern?", a: "Ja. Re-Run jederzeit möglich, alle Versionen bleiben dokumentiert." },
          ]}
        />

        <SubpageCta primaryLabel="Eigenen Bericht erstellen" />
      </main>
      <SiteFooter />
    </div>
  );
}
